import {
  IFormItemType,
  IFormItemTypeParams,
  IGetFormDataParams,
  IQuestionItem,
  IQuestionnaireRequestData,
  IQuestionType,
  isSelectType
} from 'pages/formsCreate/types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import config from 'controllers/config';
import { useApi } from 'services/api';
import { IRouterProps } from 'pages/orderHistory';
import { useSnackbar } from 'hooks';
import moment from 'moment';

type Params = IRouterProps & {
  params?: Record<string, string | undefined>;
};

export const useEditForm = ({ params, router }: Params) => {
  const { api } = useApi();

  const formId = useMemo(() => params?.id, [params]);
  const { Snackbar, toggleSnackbar } = useSnackbar();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [newQuestionRequired, setNewQuestionRequired] = useState(false);
  const [selectedSuppliers, setSelectedSuppliers] = useState<number[]>([]);
  const [questionList, setQuestionList] = useState<IQuestionItem[]>([]);

  const [questionTypes, setQuestionTypes] = useState<IFormItemType[]>([]);
  const [isLoadingFormItemType, setIsLoadingFormItemType] = useState(false);

  const [isSubmitingForm, setIsSubmitingForm] = useState(false);

  const [newQuestionTitle, setNewQuestionTitle] = useState('');
  const [newQuestionType, setNewQuestionType] = useState<IFormItemType | null>(null);

  const [isLoadingFormData, setIsLoadingFormData] = useState(false);

  const getFormData = useCallback(async ({ id }: IGetFormDataParams) => {
    const backendUrl = config.API_NODE;

    if (backendUrl) {
      const headers = { userfunctionkey: 'c80a0ae7cdf09e28c72e949ee37df341' };
      const { data } = await api.get<IQuestionnaireRequestData>(`${backendUrl}/forms/${id}`, { headers });
      return data;
    }
  }, []);

  const loadFormData = useCallback((id?: string) => {
    if (!id) return;

    setIsLoadingFormData(true);
    getFormData({ id })
      .then((data) => {
        if (!data) return;

        if (data.startDate) setStartDate(moment(data.startDate, 'DD/MM/YYYY').toDate());
        if (data.endDate) setEndDate(moment(data.endDate, 'DD/MM/YYYY').toDate());
        setTitle(data.title);
        setDescription(data.description);
        setQuestionList(data.questions);
        setNewQuestionRequired(data.required);
        if (data.supplierList) setSelectedSuppliers(data.supplierList);
      })
      .finally(() => setIsLoadingFormData(false));
  }, []);

  useEffect(() => {
    loadFormData(formId);
  }, [formId, loadFormData]);

  const validateFormData = useCallback((body: IQuestionnaireRequestData) => {
    const { title, description, startDate, endDate, questions } = body;

    const hasEmptyTitle = !title;
    const hasEmptyDescription = !description;
    const hasEmptyStartDate = !startDate;
    const hasEmptyEndDate = !endDate;

    if (hasEmptyTitle || hasEmptyDescription || hasEmptyStartDate || hasEmptyEndDate) {
      const emptyFields: string[] = [];

      if (hasEmptyTitle) emptyFields.push('título');
      if (hasEmptyDescription) emptyFields.push('descrição');
      if (hasEmptyStartDate) emptyFields.push('data de início');
      if (hasEmptyEndDate) emptyFields.push('data fim');

      const message = `Alguns campos estão vazios! Confira ${emptyFields.join(', ')}!`;

      throw new Error(message);
    }

    if (moment(startDate, 'DD/MM/YYYY').startOf('day').isBefore(moment().add(1, 'day').startOf('day'))) {
      const message = 'A data de início não pode ser anterior a amanhã';
      throw new Error(message);
    }

    if (moment(endDate, 'DD/MM/YYYY').startOf('day').isSameOrBefore(moment(startDate, 'DD/MM/YYYY').startOf('day'))) {
      const message = 'A data de fim da vigência deve ser posterior à data de início';

      throw new Error(message);
    }

    if (questions.length <= 1) {
      const message = 'É necessário adicionar ao menos duas questões!';
      throw new Error(message);
    }

    questions.forEach((question, index) => {
      questions.forEach((item, itemIndex) => {
        if (item.id === question.id) return;

        if (item.title === question.title && itemIndex !== index) {
          const message = `As questões tem títulos iguais! Questão ${index + 1} e ${itemIndex + 1}`;
          throw new Error(message);
        }
      });
    });
  }, []);

  const updateFormData = useCallback(
    async (body: IQuestionnaireRequestData) => {
      validateFormData(body);

      const backendUrl = config.API_NODE;

      if (backendUrl) {
        const headers = { userfunctionkey: 'd1e273ab8ae78c2aaca86d5a36cf7826' };
        const { data } = await api.post<IFormItemType[]>(`${backendUrl}/forms/`, body, { headers });
        return data;
      }
    },
    [router, validateFormData]
  );

  const getFormItemType = useCallback(async (params?: IFormItemTypeParams) => {
    const backendUrl = config.API_NODE;

    if (backendUrl) {
      const headers = { userfunctionkey: 'c80a0ae7cdf09e28c72e949ee37df341' };
      const { data } = await api.get<IFormItemType[]>(`${backendUrl}/forms/items/types`, { headers, params });
      return data;
    }
  }, []);

  const loadFormItemTypes = useCallback(
    (params?: IFormItemTypeParams) => {
      setIsLoadingFormItemType(true);
      getFormItemType(params)
        .then((data) => {
          if (data) setQuestionTypes(data);
        })
        .catch(() => setIsLoadingFormItemType(false))
        .finally(() => setIsLoadingFormItemType(false));
    },
    [getFormItemType]
  );

  useEffect(() => loadFormItemTypes(), [loadFormItemTypes]);

  const handleNewQuestion = () => {
    if (!newQuestionTitle) return;

    const type = (newQuestionType || questionTypes[0]).value;
    if (!type) return;

    const itemId = questionList.reduce((acc, item) => (item.id > acc ? item.id + 1 : acc + 1), 0);

    const item = {
      id: itemId,
      type,
      title: newQuestionTitle,
      options: []
    };

    setQuestionList((prev) => [...prev, item]);

    setNewQuestionTitle('');
    setNewQuestionType(questionTypes[0]);

    if (isSelectType(item.type)) {
      setIsEditModalOpen(true);
      setSeletectedQuestion(item);
    }
  };

  const handleSubmitForm = useCallback(async () => {
    setIsSubmitingForm(true);

    const body = {
      id: formId,
      title,
      description,
      required: newQuestionRequired,
      startDate: startDate?.toLocaleDateString('pt-br'),
      endDate: endDate?.toLocaleDateString('pt-br'),
      questions: questionList,
      supplierList: selectedSuppliers
    };

    await updateFormData(body)
      .then(() => {
        toggleSnackbar({
          message: 'Formulário criado com sucesso! Será publicado assim que o prazo de vigência iniciar.',
          type: 'success',
          duration: 1500
        });

        setTimeout(() => router.push('/formularios'), 1500);
      })
      .catch((error) => {
        const defaultMessage = 'Ocorreu um erro ao tentar salvar formulário!';
        toggleSnackbar({
          message: error.response?.data?.message || error.message || defaultMessage,
          type: 'error',
          duration: 1500
        });
      })
      .finally(() => setIsSubmitingForm(false));
  }, [
    formId,
    title,
    description,
    newQuestionRequired,
    startDate,
    endDate,
    questionList,
    selectedSuppliers,
    updateFormData
  ]);

  const handleCancelEdition = useCallback(() => {
    router.push('/formularios');
  }, [router]);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const handleEditModal = useCallback(() => setIsEditModalOpen(!isEditModalOpen), [isEditModalOpen]);

  const [selectedQuestion, setSeletectedQuestion] = useState<IQuestionItem<IQuestionType> | null>(null);

  return {
    formId,
    isLoadingFormData,
    title,
    setTitle,
    description,
    setDescription,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    questionList,
    setQuestionList,
    newQuestionTitle,
    setNewQuestionTitle,
    questionTypes,
    isLoadingFormItemType,
    newQuestionType,
    setNewQuestionType,
    newQuestionRequired,
    setNewQuestionRequired,
    handleNewQuestion,
    isSubmitingForm,
    handleSubmitForm,
    selectedSuppliers,
    setSelectedSuppliers,
    Snackbar,
    handleCancelEdition,
    isEditModalOpen,
    handleEditModal,
    selectedQuestion,
    setSeletectedQuestion
  };
};
